import { create } from 'zustand';

/**
 * Game states:
 * idle → betting → round_started → waiting_for_server
 * → step_success | step_failed | cashed_out → round_completed
 */

const INITIAL_STATE = {
  // Connection
  socket: null,
  connected: false,

  // Session
  mode: 'demo',          // 'demo' | 'real'
  gameId: 'rabbit-rush',
  sessionId: null,
  playerId: null,
  balance: 0,
  currency: 'DEMO',

  // Game config
  gameConfig: null,

  // Game state
  gameState: 'idle',     // see doc above
  roundId: null,
  betAmount: 1,
  difficulty: 'medium',
  currentStep: 0,
  currentMultiplier: 1,
  possibleWin: 0,
  maxSteps: 10,
  multiplierTable: [],
  failedStep: null,

  // Results
  lastOutcome: null,     // 'win' | 'lose' | 'cashout'
  lastWinAmount: 0,

  // History
  history: [],

  // Settings
  soundEnabled: true,
  animationsEnabled: true,

  // Auto Bet
  autoBet: false,
  autoBetCount: 0,
  autoBetRemaining: 0,
  autoBetStopOnWin: false,
  autoBetStopOnLoss: false,
};

const useGameStore = create((set, get) => ({
  ...INITIAL_STATE,

  // ── Setters ────────────────────────────────────────────────────────────────
  setSocket:     (socket)    => set({ socket }),
  setConnected:  (connected) => set({ connected }),

  setSession: ({ balance, currency, playerId, mode, roundActive, currentStep, currentMultiplier, difficulty, currentBet, roundId, multiplierTable, possibleWin }) =>
    set(state => {
      const betAmount = typeof currentBet !== 'undefined' && currentBet > 0
        ? currentBet
        : state.betAmount;
      return {
        balance,
        currency,
        playerId,
        mode,
        roundId: roundId ?? state.roundId,
        currentStep: typeof currentStep !== 'undefined' ? currentStep : state.currentStep,
        currentMultiplier: typeof currentMultiplier !== 'undefined' ? currentMultiplier : state.currentMultiplier,
        difficulty: difficulty ?? state.difficulty,
        betAmount,
        possibleWin: typeof possibleWin !== 'undefined' ? possibleWin : state.possibleWin,
        multiplierTable: multiplierTable ?? state.multiplierTable,
        maxSteps: (multiplierTable ?? state.multiplierTable).length || state.maxSteps,
        failedStep: null,
        lastOutcome: null,
        lastWinAmount: 0,
        gameState: roundActive ? 'round_started' : 'idle',
      };
    }),

  setGameConfig: (gameConfig) => set(state => {
    const table = gameConfig?.multipliers?.[state.difficulty] ?? state.multiplierTable;
    return {
      gameConfig,
      multiplierTable: table,
      maxSteps: table.length || state.maxSteps,
    };
  }),

  setBetAmount:  (betAmount)  => set({ betAmount }),
  setDifficulty: (difficulty) => set(state => {
    const table = state.gameConfig?.multipliers?.[difficulty] ?? state.multiplierTable;
    return {
      difficulty,
      multiplierTable: table,
      maxSteps: table.length || state.maxSteps,
    };
  }),
  setSoundEnabled:     (v) => set({ soundEnabled: v }),
  setAnimationsEnabled:(v) => set({ animationsEnabled: v }),

  setAutoBet: (config) => set({ ...config }),

  // ── Game actions ───────────────────────────────────────────────────────────
  onBetPlaced: ({ roundId, bet, difficulty, balance, multiplier, possibleWin, maxSteps, multipliers }) =>
    set({
      gameState: 'round_started',
      roundId,
      betAmount: bet,
      difficulty,
      balance,
      currentStep: 0,
      currentMultiplier: multiplier,
      possibleWin,
      maxSteps,
      multiplierTable: multipliers,
      failedStep: null,
      lastOutcome: null,
      lastWinAmount: 0,
    }),

  onStepResult: (result) => {
    const { outcome, step, multiplier, possibleWin, balance, lostAmount } = result;
    if (outcome === 'fail') {
      const entry = { type: 'lose', amount: lostAmount ?? 0, step, ts: Date.now() };
      set(state => ({
        gameState: 'step_failed',
        balance,
        currentStep: 0,
        currentMultiplier: 1,
        failedStep: step,
        lastOutcome: 'lose',
        lastWinAmount: 0,
        history: [entry, ...state.history].slice(0, 10),
      }));
    } else if (outcome === 'cashout') {
      // auto-cashout on last step
      get().onCashOutResult(result);
    } else {
      set({
        gameState: 'step_success',
        currentStep: step,
        currentMultiplier: multiplier,
        possibleWin,
        balance,
        failedStep: null,
      });
    }
  },

  onCashOutResult: ({ multiplier, winAmount, balance, step }) => {
    const entry = { type: 'cashout', amount: winAmount, step, ts: Date.now() };
    set(state => ({
      gameState: 'cashed_out',
      balance,
      currentMultiplier: multiplier,
      failedStep: null,
      lastOutcome: 'cashout',
      lastWinAmount: winAmount,
      history: [entry, ...state.history].slice(0, 10),
    }));
  },

  setGameState: (gameState) => set({ gameState }),

  resetRound: () =>
    set({
      gameState: 'idle',
      roundId: null,
      currentStep: 0,
      currentMultiplier: 1,
      possibleWin: 0,
      failedStep: null,
      lastOutcome: null,
      lastWinAmount: 0,
    }),

  // Init from URL params
  initFromUrl: () => {
    const params = new URLSearchParams(window.location.search);
    const gameId    = params.get('gameId')    || 'rabbit-rush';
    const sessionId = params.get('sessionId') || `demo_${Math.random().toString(36).slice(2)}`;
    const playerId  = params.get('playerId')  || `player_${Math.random().toString(36).slice(2)}`;
    const mode      = params.get('mode')      || 'demo';
    set({ gameId, sessionId, playerId, mode });
    return { gameId, sessionId, playerId, mode };
  },
}));

export default useGameStore;
